import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  UseControllerReturn,
  useFormContext,
} from 'react-hook-form';
import {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import {omit} from 'lodash';
import {useSchemaContext} from './use-schema-context';
import {AnyZodObject} from 'zod';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faExclamationTriangle} from '@fortawesome/pro-solid-svg-icons';
import {Box, BoxComponentProps, Stack} from '@mantine/core';
import {isRequired} from '../utils/zod';
import {ClassNames} from '@emotion/react';
import {OmitDistributive} from '../types';

// Used for the exposed Form.[Field] components
export type FieldProps<
  TAdapterProps,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = TAdapterProps & {
  label?: string | ReactNode;
  name: TName;
  fieldHint?: ReactNode;
  errorProps?: BoxComponentProps;
};

// Values passed from Field Factory to Field Adapter
export type RenderFieldProps<TAdapterProps> = {
  inputProps: TAdapterProps;
  control: UseControllerReturn;
};
type RenderField<TAdapterProps> = {
  renderAdapter: (renderProps: RenderFieldProps<TAdapterProps>) => ReactElement;
};

// Wrap input-specific props with generic adapter props.
export type FieldAdapter<TAdapterProps> = RenderFieldProps<TAdapterProps>;

export type AdapterProps<TInputProps> = OmitDistributive<
  TInputProps,
  | 'name'
  | 'value'
  | 'defaultValue'
  | 'checked'
  | 'defaultChecked'
  | 'withAsterisk'
  | 'label'
  | 'required'
  | 'error'
  | 'labelPosition'
>;

const FieldPropKeys = [
  'name',
  'control',
  'defaultValue',
  'label',
  'fieldName',
  'fieldHint',
  'errorProps',
] as const;

export const HookFormFieldFactory = <
  TAdapterProps,
  TFieldValues extends FieldValues,
>({
  renderAdapter,
}: RenderField<TAdapterProps>) => {
  return <TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
    props: FieldProps<TAdapterProps, TFieldValues, TName>,
  ) => {
    const {control} = useFormContext();
    const {schema} = useSchemaContext();
    const {name, label, fieldHint, errorProps} = props;

    const _required = isRequired(schema as AnyZodObject, name);

    const inputProps = omit(props, [
      ...FieldPropKeys,
    ]) as RenderFieldProps<TAdapterProps>['inputProps'];

    return (
      <FormField
        control={control}
        name={name}
        render={(control) => (
          <ClassNames>
            {({cx}) => (
              <Stack
                gap={0}
                className={cx(
                  'form-field',
                  control.fieldState.error && 'field-error',
                )}
              >
                {typeof label === 'string' ? (
                  <FieldLabel
                    fieldName={name}
                    label={label}
                    required={_required}
                  />
                ) : (
                  label
                )}
                {renderAdapter({inputProps, control})}
                <FieldHint fieldHint={fieldHint} />
                <Box {...errorProps}>
                  <FieldError />
                </Box>
              </Stack>
            )}
          </ClassNames>
        )}
      />
    );
  };
};

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);
const FormField = (props: ControllerProps) => {
  const value = useMemo(() => ({name: props.name}), [props.name]);

  return (
    <FormFieldContext.Provider value={value}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};
FormField.displayName = 'FormField';

type FieldLabelProps = {fieldName: string; label?: string; required?: boolean};
const FieldLabel: FC<FieldLabelProps> = ({fieldName, label, required}) => {
  const {error} = useFormField();

  if (!label) {
    return null;
  }

  return (
    <ClassNames>
      {({cx}) => (
        <label
          className={cx(
            'form-field-label',
            required && 'required',
            error && 'error',
          )}
          htmlFor={fieldName}
        >
          {label}
        </label>
      )}
    </ClassNames>
  );
};
FieldLabel.displayName = 'FieldLabel';

const FieldHint: FC<{fieldHint?: ReactNode}> = ({fieldHint}) => {
  if (!fieldHint) {
    return null;
  }

  return <div className='form-field-hint'>{fieldHint}</div>;
};
FieldHint.displayName = 'FieldHint';

const FieldError = () => {
  const {error} = useFormField();
  const body = error ? String(error?.message) : undefined;

  if (!body) {
    return null;
  }

  return (
    <div className={'form-field-error'}>
      {error?.message && (
        <span>
          <FontAwesomeIcon icon={faExclamationTriangle} fontSize={'10px'} />
          &nbsp;
          <span role='alert'>{body}</span>
        </span>
      )}
    </div>
  );
};
FieldError.displayName = 'FieldError';

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const {getFieldState, formState} = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  return {...fieldState};
};
